import { useCallback } from "react";
import { TaskType } from "@common/types/task";
import { Invoice } from "@common/types/invoice";
import { InvoiceView } from "@common/types/views/invoiceViews";
import { buildBulkInvoicesTaskView } from "@components/tasks/TaskBody/Invoices/invoicesTaskView";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useOverlays } from "contexts/OverlayContext";

interface UseInvoiceBulkCreateTaskParams {
    view: InvoiceView;
    selectedInvoices: Invoice[];
}

/**
 * Opens the create task drawer for the selected invoices: an INVOICES task
 * pinned to exactly those invoices, with the list's current columns. Invoices
 * load one page at a time and the selection is cleared on page change, so the
 * selection is always the complete set of invoices to pin.
 */
export function useInvoiceBulkCreateTask({
    view,
    selectedInvoices,
}: UseInvoiceBulkCreateTaskParams) {
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    return useCallback(() => {
        // Opens a create form the server can only refuse on save.
        if (refuseIfViewOnly() || selectedInvoices.length === 0) {
            return;
        }
        const viewConfig = buildBulkInvoicesTaskView({
            view,
            invoices: selectedInvoices,
        });
        openOverlay("createTask", {
            type: TaskType.INVOICES,
            viewConfig,
            initialValues: { entityIds: viewConfig.filters.entityIds },
        });
    }, [openOverlay, refuseIfViewOnly, selectedInvoices, view]);
}
